import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useChatStore } from '../store/chatStore';
import { roomsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

const categoryConfig = {
  General: {
    name: 'General',
    logo: '/assets/General.png',
    description: 'General discussions and conversations on any topic',
  },
  Career: {
    name: 'Career',
    logo: '/assets/Career.png',
    description: 'Connect with professionals and discuss career opportunities',
  },
  Entertainment: {
    name: 'Entertainment',
    logo: '/assets/Entertainment.png',
    description: 'Share and discuss movies, music, games, and more',
  },
  Health: {
    name: 'Health',
    logo: '/assets/Health.png',
    description: 'Wellness, fitness, and health-related discussions',
  },
};

export default function CategoryPage() {
  const { category } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const { user, anonymousUser, logout, isAuthenticated, isAnonymous, initializeAnonymous } = useAuthStore();
  const { rooms, setRooms } = useChatStore();

  const categoryInfo = category ? categoryConfig[category as keyof typeof categoryConfig] : null;

  // Initialize anonymous user if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      initializeAnonymous();
    }
  }, [isAuthenticated, initializeAnonymous]);

  useEffect(() => {
    if (!category) {
      navigate('/');
      return;
    }

    const loadRooms = async () => {
      try {
        const roomsList = await roomsApi.list(category);
        setRooms(roomsList);
      } catch (error) {
        console.error('Failed to load rooms:', error);
      }
    };
    loadRooms();
  }, [category, setRooms, navigate]);

  if (!categoryInfo) {
    return null;
  }

  const categoryRooms = rooms.filter((room) => room.category === category);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center relative">
          {/* Left: Back button */}
          <div className="w-1/3">
            <Button variant="ghost" onClick={() => navigate('/')}>
              ← Back
            </Button>
          </div>
          
          {/* Center: Logo */}
          <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-3">
            <img 
              src="/assets/TheWorldBridger.png" 
              alt="TheWorldBridger Logo" 
              className="h-12 w-auto"
            />
            <h1 className="text-2xl font-bold">TheWorldBridger</h1>
          </div>
          
          {/* Right: User actions */}
          <div className="flex items-center gap-4 w-1/3 justify-end">
            {isAuthenticated ? (
              <>
                <span className="text-sm text-muted-foreground">
                  {isAnonymous ? anonymousUser?.username : user?.username}
                </span>
                {!isAnonymous && (
                  <Button variant="outline" onClick={logout}>
                    Logout
                  </Button>
                )}
                {isAnonymous && (
                  <>
                    <Link to="/login">
                      <Button variant="outline" size="sm">Sign In</Button>
                    </Link>
                    <Link to="/register">
                      <Button size="sm">Sign Up</Button>
                    </Link>
                  </>
                )}
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="outline">Sign In</Button>
                </Link>
                <Link to="/register">
                  <Button>Sign Up</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center gap-4 mb-8">
          <img 
            src={categoryInfo.logo} 
            alt={`${categoryInfo.name} Logo`} 
            className="h-16 w-auto"
          />
          <div>
            <h2 className="text-3xl font-bold">{categoryInfo.name}</h2>
            <p className="text-muted-foreground">{categoryInfo.description}</p>
          </div>
        </div>
        
        {categoryRooms.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">
              No chat rooms available in this category yet.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {categoryRooms.map((room) => (
              <Card
                key={room.id}
                className="cursor-pointer hover:shadow-lg transition-shadow hover:border-primary"
                onClick={() => navigate(`/room/${room.id}`)}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <span>💬</span>
                    {room.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    {room.description || 'No description'}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

